import { type BooleanElement } from '../plugins/boolean';
import {
  DEFAULT_BOOLEAN_BRIGHTNESS,
  DEFAULT_BOOLEAN_CONTRAST,
} from '../plugins/boolean/utils/masking';
import {
  getConfig,
  useEditorRuntimeStore,
} from '../stores/useEditorRuntimeStore';
import { useEditorStore } from '../stores/useEditorStore';
import { getNormalizedCanvasDimensions } from '../utils/editor/canvas';

import { getElementBounds } from './bound';
import { sortElementsByLayer, type GenericElement } from './element';
import { getPlugin, resolveBooleanOrFunction } from './plugin';
import {
  getVisibleElements,
  renderElement,
  type OverwriteState,
} from './render';
import {
  getSelectedElements,
  getTransformHandlesFromCoords,
  renderHoverBorder,
  renderMultiSelectionBorder,
  renderSelectionBorder,
  renderSelectionElement,
  renderTransformHandles,
} from './selection';

const bootstrapCanvas = (payload: {
  canvas: HTMLCanvasElement;
  isTransparent?: boolean;
  normalizedHeight: number;
  normalizedWidth: number;
  scale: number;
}) => {
  const config = getConfig();
  const { canvas, normalizedHeight, normalizedWidth, scale } = payload;
  const context = canvas.getContext('2d')!;

  context.setTransform(1, 0, 0, 1, 0, 0);
  context.scale(scale, scale);

  context.save();

  if (payload.isTransparent) {
    context.clearRect(0, 0, normalizedWidth, normalizedHeight);
  } else {
    context.fillStyle = config.canvas.fillStyle;
    context.fillRect(0, 0, normalizedWidth, normalizedHeight);
  }

  context.restore();

  return context;
};

export const renderNewElementScene = () => {
  const { newCanvas, newElement, zoom } = useEditorRuntimeStore.getState();
  const scale = 1;

  if (!newCanvas) return;

  const [normalizedWidth, normalizedHeight] = getNormalizedCanvasDimensions(
    newCanvas,
    scale,
  );
  const context = bootstrapCanvas({
    canvas: newCanvas,
    isTransparent: true,
    normalizedHeight,
    normalizedWidth,
    scale,
  });

  context.save();
  context.scale(zoom.value, zoom.value);

  if (!newElement) {
    context.clearRect(0, 0, normalizedWidth, normalizedHeight);
    return;
  }

  renderElement(newElement, context);

  const state = useEditorRuntimeStore.getState();
  const config = getConfig();
  const plugin = getPlugin(newElement.type);

  if (
    plugin.selection?.renderTransformHandlesForNewElement &&
    plugin.selection?.getTransformHandles &&
    plugin.selection?.renderTransformHandles
  ) {
    const transformHandles = plugin.selection.getTransformHandles(newElement, {
      config,
      state,
    });

    if (Object.keys(transformHandles).length <= 0) return;

    context.save();
    context.translate(state.scrollX, state.scrollY);
    plugin.selection.renderTransformHandles(context, transformHandles, {
      config,
      state,
    });
    context.restore();
  }
};

const MIN_BRIGHTNESS = 0.4;

const sharedTempCanvas = document.createElement('canvas');
const sharedTempCtx = sharedTempCanvas.getContext('2d', { alpha: true })!;

const accumulatorCanvas = document.createElement('canvas');
const accumulatorCtx = accumulatorCanvas.getContext('2d', { alpha: true })!;

const applyGrayscaleBrightness = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  brightnessValue: number,
) => {
  const imageData = ctx.getImageData(x, y, width, height, {
    colorSpace: 'srgb',
  });
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue;
    const gray = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    const v = Math.min(255, gray * brightnessValue);
    data[i] = data[i + 1] = data[i + 2] = v;
  }

  ctx.putImageData(imageData, x, y);
};

export const renderBooleanElementsWithMasking = (
  booleanElements: BooleanElement[],
  context: CanvasRenderingContext2D,
  forceRerender: boolean,
  overwriteState?: OverwriteState,
) => {
  if (booleanElements.length === 0) return;

  const sortedBooleans = [...booleanElements]
    .sort(sortElementsByLayer)
    .reverse();

  const state = useEditorRuntimeStore.getState();
  const zoom = overwriteState?.zoom ?? state.zoom.value;
  const { width, height } = context.canvas;

  if (sharedTempCanvas.width !== width || sharedTempCanvas.height !== height) {
    sharedTempCanvas.width = width;
    sharedTempCanvas.height = height;
    accumulatorCanvas.width = width;
    accumulatorCanvas.height = height;
  } else {
    accumulatorCtx.setTransform(1, 0, 0, 1, 0, 0);
    accumulatorCtx.clearRect(0, 0, width, height);
  }

  const transform = context.getTransform();
  const scaleX = overwriteState ? zoom : transform.a;
  const scaleY = overwriteState ? zoom : transform.d;
  const offsetX = overwriteState?.offsetX ?? state.scrollX;
  const offsetY = overwriteState?.offsetY ?? state.scrollY;

  for (let i = 0; i < sortedBooleans.length; i++) {
    const current = sortedBooleans[i];

    const [x1, y1, x2, y2] = getElementBounds(current, {
      withRotation: false,
    });
    const pad = 5;

    const startX = Math.floor(Math.max(0, (x1 + offsetX) * scaleX - pad));
    const startY = Math.floor(Math.max(0, (y1 + offsetY) * scaleY - pad));
    const endX = Math.ceil(Math.min(width, (x2 + offsetX) * scaleX + pad));
    const endY = Math.ceil(Math.min(height, (y2 + offsetY) * scaleY + pad));

    const bw = endX - startX;
    const bh = endY - startY;

    if (bw <= 0 || bh <= 0) continue;

    const brightness = current.brightness ?? DEFAULT_BOOLEAN_BRIGHTNESS;
    const brightnessValue =
      MIN_BRIGHTNESS + (brightness / 255) * (2 - MIN_BRIGHTNESS);

    // SAFARI WORKAROUND: do not simplify this into a single source-in pass.
    //
    // We mask the rendered element against the background using globalCompositeOperation.
    // Safari requires the destination shape to be painted with 'source-over' and fully
    // committed BEFORE switching to 'source-in'. Unlike Chrome, Safari will not reliably
    // composite against pixels drawn in the same pass, so a naive "render element -> set source-in
    // -> drawImage(background)" sequence produces an empty (fully transparent) tile on Safari while
    // working fine on Chrome.
    //
    // The fix, in order:
    //   1. Paint the element shape with 'source-over' to establish opaque destination pixels.
    //   2. Only then switch to 'source-in' and draw the background; it is clipped to the shape.
    //
    // Additionally, avoid ctx.filter for the grayscale/brightness step: Safari's canvas
    // filter support (and the context-filter-polyfill) is unreliable for chained
    // grayscale()+brightness(). We do that adjustment via getImageData/putImageData in
    // applyGrayscaleBrightness() instead.

    // 1. establish the destination shape with source-over (opaque).
    //    This is the MASK: renderElement must paint only the element's silhouette here.
    sharedTempCtx.setTransform(1, 0, 0, 1, 0, 0);
    sharedTempCtx.clearRect(startX, startY, bw, bh);

    sharedTempCtx.globalCompositeOperation = 'source-over';
    sharedTempCtx.globalAlpha = 1.0;
    sharedTempCtx.scale(scaleX, scaleY);
    renderElement(current, sharedTempCtx, forceRerender, overwriteState);

    // 2. switch to source-in and draw the background; the step-1 mask clips it to the shape.
    sharedTempCtx.setTransform(1, 0, 0, 1, 0, 0);
    sharedTempCtx.globalCompositeOperation = 'source-in';
    sharedTempCtx.drawImage(
      context.canvas,
      startX,
      startY,
      bw,
      bh, // src rect
      startX,
      startY,
      bw,
      bh, // dst rect
    );

    // 3. reset composite state.
    //    NOTE: do NOT draw the background again here — a source-over drawImage at this
    //    point would paint the full tile rect back over the masked shape and unmask it.
    sharedTempCtx.globalCompositeOperation = 'source-over';

    applyGrayscaleBrightness(
      sharedTempCtx,
      startX,
      startY,
      bw,
      bh,
      brightnessValue,
    );

    const contrast = current.contrast ?? DEFAULT_BOOLEAN_CONTRAST;
    const opacity = 1.0 - (contrast / 100) * 0.2;
    const multiplyWeight = contrast / 100;
    const sourceOverWeight = 1.0 - multiplyWeight;

    sharedTempCtx.scale(scaleX, scaleY);
    if (sourceOverWeight > 0.01) {
      sharedTempCtx.globalCompositeOperation = 'source-over';
      sharedTempCtx.globalAlpha = opacity * sourceOverWeight;
      renderElement(current, sharedTempCtx, forceRerender, overwriteState);
    }
    if (multiplyWeight > 0.01) {
      sharedTempCtx.globalCompositeOperation = 'overlay';
      sharedTempCtx.globalAlpha = opacity * multiplyWeight;
      renderElement(current, sharedTempCtx, forceRerender, overwriteState);
    }

    sharedTempCtx.setTransform(1, 0, 0, 1, 0, 0);
    sharedTempCtx.globalCompositeOperation = 'destination-out';
    sharedTempCtx.globalAlpha = 1.0;
    sharedTempCtx.drawImage(
      accumulatorCanvas,
      startX,
      startY,
      bw,
      bh,
      startX,
      startY,
      bw,
      bh,
    );

    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(
      sharedTempCanvas,
      startX,
      startY,
      bw,
      bh,
      startX,
      startY,
      bw,
      bh,
    );
    context.restore();

    accumulatorCtx.setTransform(1, 0, 0, 1, 0, 0);
    accumulatorCtx.globalCompositeOperation = 'source-over';
    accumulatorCtx.globalAlpha = 1.0;
    accumulatorCtx.scale(scaleX, scaleY);
    renderElement(current, accumulatorCtx, forceRerender, overwriteState);
  }
};

export const renderStaticScene = (forceRerender = false) => {
  const { staticCanvas, zoom } = useEditorRuntimeStore.getState();
  const { elements } = useEditorStore.getState();

  if (!staticCanvas) return;

  const scale = 1;
  const [normalizedWidth, normalizedHeight] = getNormalizedCanvasDimensions(
    staticCanvas,
    scale,
  );
  const context = bootstrapCanvas({
    canvas: staticCanvas,
    normalizedHeight,
    normalizedWidth,
    scale,
  });

  context.save();
  context.scale(zoom.value, zoom.value);

  const visibleElements =
    getVisibleElements(elements).toSorted(sortElementsByLayer);

  const booleanElements: BooleanElement[] = [];
  const nonBooleanElements: GenericElement[] = [];

  for (const element of visibleElements) {
    if (element.type === 'boolean') {
      booleanElements.push(element as BooleanElement);
    } else {
      nonBooleanElements.push(element);
    }
  }

  const maxBooleanLayer = Math.max(
    ...booleanElements.map((element) => element.layer),
  );

  for (const element of nonBooleanElements) {
    if (element.layer > maxBooleanLayer) break;
    if (element.renderedById) continue;
    renderElement(element, context, forceRerender);
  }

  renderBooleanElementsWithMasking(booleanElements, context, forceRerender);

  for (const element of nonBooleanElements) {
    if (element.layer <= maxBooleanLayer) continue;
    if (element.renderedById) continue;
    renderElement(element, context, forceRerender);
  }
};

export const renderInteractiveScene = () => {
  const config = getConfig();
  const state = useEditorRuntimeStore.getState();
  const elementState = useEditorStore.getState();
  const scale = 1;

  if (!state.interactiveCanvas) return;

  const [normalizedWidth, normalizedHeight] = getNormalizedCanvasDimensions(
    state.interactiveCanvas,
    scale,
  );
  const context = bootstrapCanvas({
    canvas: state.interactiveCanvas,
    isTransparent: true,
    normalizedHeight,
    normalizedWidth,
    scale,
  });

  context.save();
  context.scale(state.zoom.value, state.zoom.value);

  if (elementState.hoveredElementId) {
    const hoveredElement = elementState.elements.get(
      elementState.hoveredElementId,
    );
    if (
      hoveredElement &&
      !elementState.selectedElementIds.includes(hoveredElement.id)
    ) {
      renderHoverBorder(context, hoveredElement);
    }
  }

  if (state.selectionElement) {
    renderSelectionElement(state.selectionElement, context);
  }

  const selectedElements = getSelectedElements();

  selectedElements.forEach((element) => {
    const plugin = getPlugin(element.type);

    if (
      getPlugin(element.type).selection?.hasVisiblePointHandles &&
      elementState.polygonPointsVisible
    ) {
      return;
    }

    if (selectedElements.length === 1) {
      if (plugin.selection?.renderSelectionBorder) {
        return plugin.selection.renderSelectionBorder(context, element, {
          config,
          state,
        });
      }

      if (plugin.selection?.hideDefaultSelectionBorder) {
        const shouldHideDefaultSelectionBorder = resolveBooleanOrFunction(
          plugin.selection.hideDefaultSelectionBorder,
          { elementState },
        );

        if (shouldHideDefaultSelectionBorder) return;
      }
    }

    renderSelectionBorder(context, element);
  });

  context.save();
  context.translate(state.scrollX, state.scrollY);

  const plugins = state.plugins;
  if (plugins) {
    plugins.forEach((plugin) => {
      plugin.render?.drawInteractiveOverlay?.(context, {
        config,
        elementState,
        runtimeState: state,
      });
    });
  }

  if (selectedElements.length === 1) {
    const selectedElement = selectedElements[0];
    const plugin = getPlugin(selectedElement.type);

    if (
      plugin.selection?.getTransformHandles &&
      plugin.selection.renderTransformHandles
    ) {
      const transformHandles = plugin.selection.getTransformHandles(
        selectedElement,
        { config, state },
      );
      plugin.selection.renderTransformHandles(context, transformHandles, {
        config,
        state,
      });
    }

    const shouldHideDefaultTransformHandles = resolveBooleanOrFunction(
      plugin.selection?.hideDefaultTransformHandles ?? false,
      { elementState },
    );

    if (!shouldHideDefaultTransformHandles) {
      const defaultTransformHandles = getTransformHandlesFromCoords(
        getElementBounds(selectedElement, { withRotation: false }),
        selectedElement.angle,
      );

      renderTransformHandles(context, defaultTransformHandles);
    }
  }

  if (
    selectedElements.length > 1 &&
    state.activeTransformation?.type !== 'rotation'
  ) {
    const transformHandles = getTransformHandlesFromCoords(
      renderMultiSelectionBorder(context, selectedElements),
      0,
    );
    renderTransformHandles(context, transformHandles);
  }

  context.restore();
};

export const hideNewElementCanvas = () => {
  const { newCanvas } = useEditorRuntimeStore.getState();
  if (newCanvas) {
    newCanvas.style.display = 'none';
  }
};

export const showNewElementCanvas = () => {
  const { newCanvas } = useEditorRuntimeStore.getState();
  if (newCanvas) {
    newCanvas.style.display = 'block';
  }
};
